// GenToken/RegisterMetadata.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RegisterMetadata.css';
import { truncateAddress } from './tokenUtils';

const RegisterMetadata = ({ 
  tokenAddress, 
  tokenName, 
  tokenSymbol, 
  network, 
  language,
  onClose, 
  onSuccess 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [metadata, setMetadata] = useState({
    name: '',
    symbol: '',
    description: '',
    image: '',
    externalUrl: '',
  });
  
  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';

  // 토큰 정보가 변경될 때 메타데이터 초기값 설정
  useEffect(() => {
    setMetadata({
      name: tokenName || '',
      symbol: tokenSymbol || '',
      description: '',
      image: '',
      externalUrl: '',
    });
    
    // 이미 메타데이터가 있는지 확인 (서버에서 확인)
    const fetchMetadata = async () => {
      try {
        const response = await axios.get(`${SERVER_ROOT}/api/solana/token/metadata/${tokenAddress}`);
        if (response.data && response.data.metadata) {
          const md = response.data.metadata;
          setMetadata({
            name: md.name || tokenName || '',
            symbol: md.symbol || tokenSymbol || '',
            description: md.description || '',
            image: md.image || '',
            externalUrl: md.external_url || '',
          });
        }
      } catch (err) {
        console.log('No existing metadata or error fetching:', err);
        // 오류가 있어도 무시하고 기본값 사용
      }
    };
    
    if (tokenAddress) {
      fetchMetadata();
    }
  }, [tokenAddress, tokenName, tokenSymbol, SERVER_ROOT]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMetadata(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit metadata
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate input
      if (!metadata.name || !metadata.symbol) {
        throw new Error(language === 'KR' 
          ? '토큰 이름과 심볼은 필수입니다.' 
          : 'Token name and symbol are required.');
      }

      // Call API to register metadata
      const response = await axios.post(`${SERVER_ROOT}/api/solana/token/metadata`, {
        tokenAddress,
        network,
        metadata: {
          name: metadata.name,
          symbol: metadata.symbol,
          description: metadata.description,
          image: metadata.image,
          external_url: metadata.externalUrl,
        }
      });

      if (response.data && response.data.success) {
        setSuccess(true);
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(response.data);
          }, 2000);
        }
      } else {
        throw new Error(response.data?.error || 'Unknown error occurred');
      }
    } catch (err) {
      console.error('Error registering metadata:', err);
      setError(err.response?.data?.error || err.message || 'Failed to register metadata');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="metadata-register-container success-container">
        <div className="success-message">
          <h3>
            {language === 'KR' 
              ? '메타데이터 등록 성공!' 
              : 'Metadata Registration Successful!'}
          </h3>
          <p>
            {language === 'KR' 
              ? '토큰 메타데이터가 성공적으로 등록되었습니다.' 
              : 'Your token metadata has been successfully registered.'}
          </p>
          <button className="close-button" onClick={onClose}>
            {language === 'KR' ? '닫기' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="metadata-register-container">
      <div className="metadata-register-header">
        <h3>
          {language === 'KR' 
            ? '토큰 메타데이터 등록' 
            : 'Register Token Metadata'}
        </h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="token-info">
        <p>
          {language === 'KR' ? '토큰 주소' : 'Token Address'}: 
          <span className="token-address">{truncateAddress(tokenAddress, 8, 8)}</span>
          <button 
            className="copy-button"
            onClick={() => {
              navigator.clipboard.writeText(tokenAddress);
              alert(language === 'KR' ? '주소가 복사되었습니다!' : 'Address copied!');
            }}
          >
            {language === 'KR' ? '복사' : 'Copy'}
          </button>
        </p>
        <p>
          {language === 'KR' ? '네트워크' : 'Network'}: 
          <span className="network-name">{network}</span>
        </p>
      </div>

      <form className="metadata-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            {language === 'KR' ? '토큰 이름' : 'Token Name'}: *
          </label>
          <input 
            type="text" 
            id="name" 
            name="name" 
            value={metadata.name}
            onChange={handleInputChange}
            placeholder={language === 'KR' ? '토큰 이름' : 'Token Name'}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="symbol">
            {language === 'KR' ? '토큰 심볼' : 'Token Symbol'}: *
          </label>
          <input 
            type="text" 
            id="symbol" 
            name="symbol" 
            value={metadata.symbol}
            onChange={handleInputChange}
            placeholder={language === 'KR' ? '토큰 심볼' : 'Token Symbol'}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">
            {language === 'KR' ? '설명' : 'Description'}:
          </label>
          <textarea 
            id="description" 
            name="description" 
            value={metadata.description}
            onChange={handleInputChange}
            placeholder={language === 'KR' ? '토큰 설명' : 'Token Description'}
            rows="3"
          />
        </div>

        <div className="form-group">
          <label htmlFor="image">
            {language === 'KR' ? '이미지 URL' : 'Image URL'}:
          </label>
          <input 
            type="text" 
            id="image" 
            name="image" 
            value={metadata.image}
            onChange={handleInputChange}
            placeholder={language === 'KR' ? '토큰 이미지 URL' : 'Token Image URL'}
          />
          <small className="help-text">
            {language === 'KR' 
              ? '토큰 이미지의 URL을 입력하세요. (예: https://example.com/image.png)' 
              : 'Enter URL for token image. (e.g., https://example.com/image.png)'}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="externalUrl">
            {language === 'KR' ? '외부 URL' : 'External URL'}:
          </label>
          <input 
            type="text" 
            id="externalUrl" 
            name="externalUrl" 
            value={metadata.externalUrl}
            onChange={handleInputChange}
            placeholder={language === 'KR' ? '토큰 웹사이트 URL' : 'Token Website URL'}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="register-button"
            disabled={isLoading}
          >
            {isLoading 
              ? (language === 'KR' ? '처리 중...' : 'Processing...') 
              : (language === 'KR' ? '메타데이터 등록' : 'Register Metadata')}
          </button>
          <button 
            type="button" 
            className="cancel-button"
            onClick={onClose}
            disabled={isLoading}
          >
            {language === 'KR' ? '취소' : 'Cancel'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterMetadata;